import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useVideo } from "./VideoContext";
import { safeSetItem } from "../utils/safeStorage";

const SubtitleSettingsContext = createContext();

export const useSubtitleSettings = () => useContext(SubtitleSettingsContext);

// کلید localStorage
const STORAGE_KEY = "subtitleSettings";

// زبان‌های پشتیبانی شده
export const LANGUAGES = [
  { code: "en", name: "English", direction: "ltr", font: "Roboto" },
  { code: "fa", name: "فارسی", direction: "rtl", font: "Vazirmatn" },
  { code: "de", name: "Deutsch", direction: "ltr", font: "Roboto" },
  { code: "fr", name: "Français", direction: "ltr", font: "Roboto" },
  { code: "it", name: "Italiano", direction: "ltr", font: "Roboto" },
  { code: "tr", name: "Türkçe", direction: "ltr", font: "Roboto" },
];

// حالت‌های نمایش زیرنویس
export const DISPLAY_MODES = {
  SHOW: "show", // نمایش عادی
  HIDDEN: "hidden", // پنهان
  UNDERCOVER: "undercover", // زیر کاور (نمایش با هاور)
};

// مقادیر پیش‌فرض
const DEFAULT_SETTINGS = {
  subtitle1: {
    language: "en",
    displayMode: DISPLAY_MODES.SHOW,
    fontSize: 18,
    color: "#04ff00", // سبز برای زیرنویس یک
    bgColor: "rgba(0, 0, 0, 0.85)",
    showTranslateButton: false,
    showSaveButton: false,
    showEyeButton: true,
    translateService: "bing",
  },
  subtitle2: {
    language: "fa",
    displayMode: DISPLAY_MODES.SHOW,
    fontSize: 18,
    color: "#fbff00", // زرد برای زیرنویس دو
    bgColor: "rgba(0, 0, 0, 0.85)", // فقط یکبار (اونوقت دوبار نوشته بودی)
    showTranslateButton: false,
    showEyeButton: true,
    translateService: "bing",
  },
};

export const SubtitleSettingsProvider = ({ children }) => {
  const {
    subtitle1Loaded,
    subtitle2Loaded,
    setSubtitle1Text,
    setSubtitle2Text,
    showSubtitles,
    setShowSubtitles,
  } = useVideo();

  // ============== بارگذاری از localStorage ==============
  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error("Error loading subtitle settings:", error);
    }
    return DEFAULT_SETTINGS;
  };

  const [settings, setSettings] = useState(loadSettings);

  // ============== حالت‌های UI پنل‌ها ==============
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showSelectorPanel, setShowSelectorPanel] = useState(false);
  const [showStylePanel, setShowStylePanel] = useState(false);

  // ============== وضعیت زیر کاور ==============
  const [undercoverActive, setUndercoverActive] = useState({
    1: false,
    2: false,
  });

  // ============== ذخیره در localStorage ==============
  useEffect(() => {
    safeSetItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // ============== به‌روزرسانی تنظیمات ==============
  const updateSubtitleSetting = useCallback((subNumber, field, value) => {
    setSettings((prev) => ({
      ...prev,
      [`subtitle${subNumber}`]: {
        ...prev[`subtitle${subNumber}`],
        [field]: value,
      },
    }));
  }, []);

  // ============== ریست به پیش‌فرض ==============
  const resetToDefault = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  // ============== اعمال تنظیمات روی زیرنویس‌ها ==============
  const applySubtitleStyles = useCallback(
    (subNumber) => {
      const subSettings = settings[`subtitle${subNumber}`];
      if (!subSettings) return;

      // اینجا می‌تونیم استایل‌ها رو به CSS variables یا مستقیماً به المان‌ها اعمال کنیم
      document.documentElement.style.setProperty(
        `--subtitle${subNumber}-font-size`,
        `${subSettings.fontSize}px`
      );
      document.documentElement.style.setProperty(
        `--subtitle${subNumber}-color`,
        subSettings.color
      );
      document.documentElement.style.setProperty(
        `--subtitle${subNumber}-bg-color`,
        subSettings.bgColor
      );
      document.documentElement.style.setProperty(
        `--subtitle${subNumber}-font-family`,
        LANGUAGES.find((l) => l.code === subSettings.language)?.font ||
          "inherit"
      );
      document.documentElement.style.setProperty(
        `--subtitle${subNumber}-direction`,
        LANGUAGES.find((l) => l.code === subSettings.language)?.direction ||
          "ltr"
      );
    },
    [settings]
  );

  // اعمال استایل‌ها هنگام تغییر تنظیمات
  useEffect(() => {
    if (subtitle1Loaded) applySubtitleStyles(1);
    if (subtitle2Loaded) applySubtitleStyles(2);
  }, [settings, subtitle1Loaded, subtitle2Loaded, applySubtitleStyles]);

  // ============== توابع شورتکات ==============
  const toggleSubtitle1Display = useCallback(() => {
    if (settings.subtitle1.displayMode === DISPLAY_MODES.UNDERCOVER) return;

    const newMode =
      settings.subtitle1.displayMode === DISPLAY_MODES.SHOW
        ? DISPLAY_MODES.HIDDEN
        : DISPLAY_MODES.SHOW;

    updateSubtitleSetting(1, "displayMode", newMode);
  }, [settings.subtitle1.displayMode, updateSubtitleSetting]);

  const toggleSubtitle2Display = useCallback(() => {
    if (settings.subtitle2.displayMode === DISPLAY_MODES.UNDERCOVER) return;

    const newMode =
      settings.subtitle2.displayMode === DISPLAY_MODES.SHOW
        ? DISPLAY_MODES.HIDDEN
        : DISPLAY_MODES.SHOW;

    updateSubtitleSetting(2, "displayMode", newMode);
  }, [settings.subtitle2.displayMode, updateSubtitleSetting]);

  const increaseFontSize1 = useCallback(() => {
    updateSubtitleSetting(1, "fontSize", settings.subtitle1.fontSize + 2);
  }, [settings.subtitle1.fontSize, updateSubtitleSetting]);

  const decreaseFontSize1 = useCallback(() => {
    updateSubtitleSetting(
      1,
      "fontSize",
      Math.max(10, settings.subtitle1.fontSize - 2)
    );
  }, [settings.subtitle1.fontSize, updateSubtitleSetting]);

  const increaseFontSize2 = useCallback(() => {
    updateSubtitleSetting(2, "fontSize", settings.subtitle2.fontSize + 2);
  }, [settings.subtitle2.fontSize, updateSubtitleSetting]);

  const decreaseFontSize2 = useCallback(() => {
    updateSubtitleSetting(
      2,
      "fontSize",
      Math.max(10, settings.subtitle2.fontSize - 2)
    );
  }, [settings.subtitle2.fontSize, updateSubtitleSetting]);

  // ============== تعیین وضعیت نهایی نمایش با توجه به تکنیک سایه ==============
  // const getEffectiveDisplayMode = useCallback(
  //   (subNumber, shadowMode = null) => {
  //     // اگر تکنیک سایه فعال باشه و حالتی تعیین کرده باشه، اولویت با اونه
  //     if (shadowMode) return shadowMode;

  //     // وگرنه تنظیمات کاربر
  //     return (
  //       settings[`subtitle${subNumber}`]?.displayMode || DISPLAY_MODES.SHOW
  //     );
  //   },
  //   [settings],
  // );

  // ============== مدیریت زیر کاور ==============
  const setUndercoverHover = useCallback((subNumber, isHovering) => {
    setUndercoverActive((prev) => ({ ...prev, [subNumber]: isHovering }));
  }, []);

  const [temporaryOverride, setTemporaryOverride] = useState({
    1: null,
    2: null,
  });

  const setTemporarySubtitleMode = useCallback((subNumber, mode) => {
    setTemporaryOverride((prev) => ({ ...prev, [subNumber]: mode }));
  }, []);

  const clearTemporaryOverride = useCallback((subNumber) => {
    setTemporaryOverride((prev) => ({ ...prev, [subNumber]: null }));
  }, []);

  const clearAllTemporaryOverrides = useCallback(() => {
    setTemporaryOverride({ 1: null, 2: null });
  }, []);

  // تابع getEffectiveDisplayMode رو هم به‌روزرسانی کنید:
  const getEffectiveDisplayMode = useCallback(
    (subNumber, shadowMode = null) => {
      // اولویت 1: override موقت (مثل تکنیک سایه)
      if (temporaryOverride[subNumber] !== null) {
        return temporaryOverride[subNumber];
      }

      // اولویت 2: حالت تعیین شده توسط تکنیک سایه
      if (shadowMode) return shadowMode;

      // اولویت 3: تنظیمات کاربر
      return (
        settings[`subtitle${subNumber}`]?.displayMode || DISPLAY_MODES.SHOW
      );
    },
    [settings, temporaryOverride]
  );

  const value = {
    // تنظیمات
    settings,
    updateSubtitleSetting,
    resetToDefault,

    // حالت‌های پنل
    showSettingsPanel,
    setShowSettingsPanel,
    showSelectorPanel,
    setShowSelectorPanel,
    showStylePanel,
    setShowStylePanel,

    // توابع کاربردی
    getEffectiveDisplayMode,
    undercoverActive,
    setUndercoverHover,

    // شورتکات‌ها
    toggleSubtitle1Display,
    toggleSubtitle2Display,
    increaseFontSize1,
    decreaseFontSize1,
    increaseFontSize2,
    decreaseFontSize2,

    setTemporarySubtitleMode,
    clearTemporaryOverride,
    clearAllTemporaryOverrides,

    // ثابت‌ها
    LANGUAGES,
    DISPLAY_MODES,
  };

  return (
    <SubtitleSettingsContext.Provider value={value}>
      {children}
    </SubtitleSettingsContext.Provider>
  );
};
